import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getReadiness } from '../api.js';
import type { Readiness } from '../api.js';

// THE CHECKLIST THAT NAMES THE NEXT STEP.
//
// Kyle's ask: after the ICP and the personas and picking who to reach, the app
// should tell you what to do next. It never did — it showed six screens, each
// able to say "nothing yet", and left the operator to work out the order.
//
// The order is not a preference, it is a DEPENDENCY CHAIN. You cannot find
// contacts before you have accounts, or write a cadence before you have someone
// to write to, or send before a domain is verified. Presenting six equal
// options is how someone picks the one that cannot work yet and concludes the
// app is broken.
//
// So: exactly one step is live at a time, the ones behind it are done, and the
// ones ahead are visibly waiting. It disappears entirely when the chain is
// complete — a setup checklist that outlives setup is clutter.

export function Setup({ compact }: { compact?: boolean }) {
  const [r, setR] = useState<Readiness | null>(null);

  useEffect(() => {
    // A failed readiness check renders NOTHING rather than nothing-to-do —
    // but say so in the console; the checklist reappearing after a retry is
    // the recovery, silence was the bug.
    getReadiness().then((x) => setR(x.readiness))
      .catch((e) => console.warn('[setup] readiness unavailable:', e?.message));
  }, []);

  if (!r || !r.next) return null;

  const doneCount = r.steps.filter((s) => s.done).length;

  // ONE WARM CTA PER VIEW — the brand rule, and this block sits on Today above
  // a queue whose items carry warm primaries of their own. Warm is only safe
  // here while there are no accounts, because with no accounts the queue is
  // definitionally empty and this is the only action on the page. The moment
  // real work exists, the queue owns warm and setup steps back to navy.
  const ownsWarm = !r.steps.find((s) => s.key === 'accounts')?.done;

  return (
    <section className={`setup ${compact ? 'setup-compact' : ''} `
      + `${ownsWarm ? 'setup-lead' : ''}`}>
      <header>
        <h4>Get to your first send</h4>
        <span className="meta">{doneCount} of {r.steps.length}</span>
      </header>

      <ol className="setuplist">
        {r.steps.map((s, i) => {
          // The first unfinished step is the only live one. Everything after it
          // is greyed rather than hidden, so the shape of the work is visible
          // without inviting you to start in the middle.
          const firstOpen = r.steps.findIndex((x) => !x.done);
          const state = s.done ? 'done' : i === firstOpen ? 'now' : 'later';
          return (
            <li key={s.key} className={`setupstep ${state}`}>
              <span className="setupmark" aria-hidden="true">
                {s.done ? '✓' : i === firstOpen ? '→' : ''}
              </span>
              <div className="setupbody">
                <strong>{s.label}</strong>
                {state === 'now' && <span className="setupwhy">{s.why}</span>}
              </div>
              {state === 'now' && (
                <Link className="setupgo" to={s.href}>Start</Link>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
